import { useState, useEffect } from 'react';
import { ChevronDown, User, Clock, AlertCircle } from 'lucide-react';
import { STAGES, STAGE_LABEL, STAGE_COLORS, relTime, api } from './api';

// #1083 Phase 2/C — Score-Schwellen angepasst: heiß 75+, warm 50+, kalt <50
function ScoreBadge({ score }) {
  const s = score ?? 0;
  const color = s >= 75 ? 'bg-emerald-500' : s >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const label = s >= 75 ? 'heiß' : s >= 50 ? 'warm' : 'kalt';
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--bg-inset)] border border-[var(--border-card)]" title={`Score: ${s} (${label})`}>
      <span className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-[12px] text-[var(--text-tertiary)]">Score</span>
      <span className="text-[13px] font-semibold text-[var(--text-primary)]">{s}</span>
    </span>
  );
}

function StagePill({ stage, leadId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const change = async (next) => {
    if (next === stage) { setOpen(false); return; }
    setBusy(true);
    try {
      await api(`/api/leads/${leadId}`, { method: 'PUT', body: { stage: next } });
      onChanged?.();
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const colorClass = STAGE_COLORS[stage] || STAGE_COLORS.neu;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] font-medium ${colorClass} hover:opacity-80 disabled:opacity-50`}
      >
        {STAGE_LABEL[stage] || stage}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 top-full left-0 mt-1 min-w-[180px] bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg shadow-md py-1">
            {STAGES.map((s) => (
              <button
                key={s}
                onClick={() => change(s)}
                className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-[var(--bg-inset)] ${s === stage ? 'font-semibold text-[#338edc]' : 'text-[var(--text-primary)]'}`}
              >
                {STAGE_LABEL[s] || s}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function LastContactPill({ at }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--bg-inset)] border border-[var(--border-card)] text-[12px]">
      <Clock className="w-3 h-3 text-[var(--text-tertiary)]" />
      <span className="text-[var(--text-tertiary)]">Letzter Kontakt</span>
      <span className="text-[var(--text-primary)] font-medium">{relTime(at)}</span>
    </span>
  );
}

function DaysSinceReplyPill({ days }) {
  if (days == null) return null;
  let cls = 'bg-[var(--bg-inset)] text-[var(--text-secondary)] border-[var(--border-card)]';
  if (days > 7) cls = 'bg-red-50 text-red-700 border-red-200';
  else if (days > 3) cls = 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] font-medium ${cls}`}>
      {days > 3 && <AlertCircle className="w-3 h-3" />}
      {days} Tag{days === 1 ? '' : 'e'} ohne Antwort
    </span>
  );
}

// #1525 — Owner-Pill schreibend: zuweisen über POST /api/leads/:id/zuweisen (mitarbeiter.id).
function OwnerPill({ name, currentId, leadId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState([]);
  const initial = (name || '?').trim().charAt(0).toUpperCase();

  useEffect(() => {
    if (!open || options.length) return;
    let active = true;
    api('/api/leads/mitarbeiter')
      .then((rows) => { if (active) setOptions(Array.isArray(rows) ? rows : []); })
      .catch(() => {});
    return () => { active = false; };
  }, [open, options.length]);

  const assign = async (mitarbeiterId) => {
    if (mitarbeiterId === currentId) { setOpen(false); return; }
    setBusy(true);
    try {
      await api(`/api/leads/${leadId}/zuweisen`, { method: 'POST', body: { mitarbeiter_id: mitarbeiterId } });
      onChanged?.();
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy || !leadId}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--bg-inset)] border border-[var(--border-card)] text-[12px] hover:opacity-80 disabled:opacity-50"
      >
        <span className="w-5 h-5 rounded-full bg-[#338edc] text-white flex items-center justify-center text-[10px] font-semibold">
          {initial}
        </span>
        <span className="text-[var(--text-primary)] font-medium">{name || 'Nicht zugewiesen'}</span>
        <ChevronDown className="w-3 h-3 text-[var(--text-tertiary)]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 top-full left-0 mt-1 min-w-[200px] max-h-[260px] overflow-y-auto bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg shadow-md py-1">
            {options.length === 0 && (
              <div className="px-3 py-1.5 text-[12px] text-[var(--text-tertiary)]">Lädt…</div>
            )}
            {options.map((m) => (
              <button
                key={m.id}
                onClick={() => assign(m.id)}
                className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-[var(--bg-inset)] ${m.id === currentId ? 'font-semibold text-[#338edc]' : 'text-[var(--text-primary)]'}`}
              >
                {m.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function KPIPills({ lead, summary, onChanged }) {
  const lastContact = summary?.last_contact || lead?.updated_at;
  const daysSinceReply = summary?.days_since_last_activity;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ScoreBadge score={lead?.score} />
      <StagePill stage={lead?.stage || 'neu'} leadId={lead?.id} onChanged={onChanged} />
      <LastContactPill at={lastContact} />
      <DaysSinceReplyPill days={daysSinceReply} />
      <OwnerPill name={lead?.zugewiesen_name} currentId={lead?.zugewiesen_an} leadId={lead?.id} onChanged={onChanged} />
    </div>
  );
}
